import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { getDashboardStats, listNegotiations, getRecommendations, getSalesPerformance } from '../lib/api';
import { formatDate, getReputationColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import {
  Activity,
  AlertTriangle,
  CalendarCheck2,
  BarChart3,
  DollarSign,
  FileText,
  MessageSquare,
  TrendingUp,
  ShieldAlert,
  Users,
  ArrowRight,
  Lightbulb,
  Search,
  ShoppingBag,
  UserRoundCog,
} from 'lucide-react';
import LegacyBanner from '../components/common/LegacyBanner';

const quickLinks = (lang) => [
  { id: 'negotiations', label: lang === 'pl' ? 'Negocjacje' : 'Negotiations', to: '/negotiations', icon: MessageSquare },
  { id: 'pipeline', label: lang === 'pl' ? 'Pipeline' : 'Pipeline', to: '/pipeline', icon: BarChart3 },
  { id: 'contractors', label: lang === 'pl' ? 'Kontrahenci' : 'Contractors', to: '/contractors', icon: Users },
  { id: 'research', label: lang === 'pl' ? 'Research' : 'Research', to: '/research', icon: Search },
  { id: 'products', label: lang === 'pl' ? 'Produkty' : 'Products', to: '/products', icon: ShoppingBag },
  { id: 'reports', label: lang === 'pl' ? 'Raporty' : 'Reports', to: '/reports', icon: FileText },
];

const recommendationColors = {
  high: 'bg-red-400/10 text-red-400 border-red-400/20',
  medium: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
  low: 'bg-blue-400/10 text-blue-400 border-blue-400/20',
};

function compactCurrency(value) {
  const num = Number(value || 0);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return `${Math.round(num)}`;
}

export default function Dashboard({ lang, tenantId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState(null);
  const [negotiations, setNegotiations] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [statsData, salesData, negotiationsData, recommendationsData] = await Promise.all([
        getDashboardStats(tenantId).catch(() => null),
        getSalesPerformance(tenantId).catch(() => null),
        listNegotiations(tenantId).catch(() => []),
        getRecommendations(tenantId).catch(() => []),
      ]);

      setStats(statsData);
      setSales(salesData);
      setNegotiations(Array.isArray(negotiationsData) ? negotiationsData.slice(0, 6) : []);
      setRecommendations(Array.isArray(recommendationsData) ? recommendationsData.slice(0, 6) : []);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const kpis = useMemo(() => {
    const revenue = sales?.revenue?.total || 0;
    const pipeline = sales?.revenue?.pipeline_value || 0;
    const winRate = sales?.win_rate?.all_time || 0;
    const reputation = Math.round((stats?.avg_contractor_reputation || 0) * 100);
    return [
      {
        id: 'revenue',
        icon: DollarSign,
        label: lang === 'pl' ? 'Łączny przychód' : 'Total revenue',
        value: `${compactCurrency(revenue)} PLN`,
        helper: lang === 'pl' ? 'Wszystkie zamknięte deale' : 'All closed deals',
      },
      {
        id: 'pipeline',
        icon: BarChart3,
        label: lang === 'pl' ? 'Wartość pipeline' : 'Pipeline value',
        value: `${compactCurrency(pipeline)} PLN`,
        helper: lang === 'pl' ? 'Aktywne szanse sprzedaży' : 'Active sales opportunities',
      },
      {
        id: 'win-rate',
        icon: TrendingUp,
        label: lang === 'pl' ? 'Win rate' : 'Win rate',
        value: `${winRate}%`,
        helper: lang === 'pl' ? 'Skuteczność negocjacji' : 'Negotiation success',
      },
      {
        id: 'risk',
        icon: AlertTriangle,
        label: lang === 'pl' ? 'Negocjacje ryzykowne' : 'At-risk negotiations',
        value: `${stats?.at_risk_negotiations || 0}`,
        helper: lang === 'pl' ? 'Wymagają interwencji' : 'Need intervention',
      },
      {
        id: 'active',
        icon: MessageSquare,
        label: lang === 'pl' ? 'Aktywne negocjacje' : 'Active negotiations',
        value: `${stats?.active_negotiations || 0}`,
        helper: lang === 'pl' ? 'Otwarte rozmowy' : 'Open conversations',
      },
      {
        id: 'reputation',
        icon: Users,
        label: lang === 'pl' ? 'Reputacja kontrahentów' : 'Contractor reputation',
        value: `${reputation}%`,
        helper: lang === 'pl' ? 'Średnia jakość relacji' : 'Average relationship quality',
        valueClass: getReputationColor((stats?.avg_contractor_reputation || 0)),
      },
    ];
  }, [lang, sales, stats]);

  const executiveAlerts = useMemo(() => {
    const alerts = [];
    const riskCount = stats?.at_risk_negotiations || 0;
    const highPriorityRecs = recommendations.filter((r) => r.priority === 'high');
    const winRate = Number(sales?.win_rate?.all_time || 0);

    if (riskCount > 0) {
      alerts.push({
        id: 'risk',
        level: 'high',
        title: lang === 'pl' ? `Ryzykowne negocjacje: ${riskCount}` : `At-risk negotiations: ${riskCount}`,
        action: lang === 'pl' ? 'Przejdź do negocjacji' : 'Open negotiations',
        onClick: () => navigate('/negotiations'),
      });
    }
    if (highPriorityRecs.length > 0) {
      alerts.push({
        id: 'recs',
        level: 'medium',
        title: lang === 'pl'
          ? `Wysoki priorytet AI: ${highPriorityRecs.length}`
          : `High-priority AI items: ${highPriorityRecs.length}`,
        action: lang === 'pl' ? 'Sprawdź rekomendacje' : 'Review recommendations',
        onClick: () => navigate('/reports'),
      });
    }
    if (winRate > 0 && winRate < 30) {
      alerts.push({
        id: 'win-rate',
        level: 'medium',
        title: lang === 'pl' ? 'Niski win rate - potrzebna korekta strategii' : 'Low win rate - strategy adjustment needed',
        action: lang === 'pl' ? 'Otwórz Strategy Planner' : 'Open Strategy Planner',
        onClick: () => navigate('/strategy'),
      });
    }
    return alerts.slice(0, 4);
  }, [lang, navigate, recommendations, sales?.win_rate?.all_time, stats?.at_risk_negotiations]);

  const todayPlan = useMemo(() => {
    const plan = [];
    recommendations.forEach((rec, idx) => {
      plan.push({
        id: `rec-${idx}`,
        title: rec.title || (lang === 'pl' ? 'Przeanalizuj rekomendację AI' : 'Review AI recommendation'),
        hint: rec.priority ? `${lang === 'pl' ? 'Priorytet' : 'Priority'}: ${rec.priority}` : '',
      });
    });
    (stats?.recent_activity || []).slice(0, 2).forEach((act, idx) => {
      plan.push({
        id: `act-${idx}`,
        title: lang === 'pl' ? `Sprawdź aktywność: ${act.contractor_name || 'kontrahent'}` : `Review activity: ${act.contractor_name || 'contractor'}`,
        hint: formatDate(act.timestamp),
      });
    });
    return plan.slice(0, 5);
  }, [lang, recommendations, stats?.recent_activity]);

  const roleCard = useMemo(() => {
    const role = currentUser?.role || 'user';
    if (role === 'admin') {
      return {
        title: lang === 'pl' ? 'Tryb administratora' : 'Administrator mode',
        description: lang === 'pl'
          ? 'Monitoruj ryzyko, KPI i obciążenie zespołu.'
          : 'Monitor risk, KPI and team workload.',
        action: lang === 'pl' ? 'Przejdź do ustawień' : 'Open settings',
        onClick: () => navigate('/settings'),
      };
    }
    return {
      title: lang === 'pl' ? 'Tryb handlowca' : 'Sales mode',
      description: lang === 'pl'
        ? 'Skup się na negocjacjach i szybkim domykaniu tematów.'
        : 'Focus on negotiations and fast deal progression.',
      action: lang === 'pl' ? 'Otwórz pipeline' : 'Open pipeline',
      onClick: () => navigate('/pipeline'),
    };
  }, [currentUser?.role, lang, navigate]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}><CardContent className="p-5"><Skeleton className="h-24 w-full" /></CardContent></Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <LegacyBanner
        replacement="Pulpit (HomeV3)"
        replacementPath="/home-v3"
        lang={lang}
        reason={lang === 'pl'
          ? 'Ten panel pochodzi z Adama v1 (CRM negocjacyjny). W obecnej wersji MASKIL główny widok to Pulpit (HomeV3) z briefingiem dnia + Mission Board.'
          : 'This panel is from Adam v1 (negotiation CRM). The current MASKIL main view is the Briefing Dashboard (HomeV3).'}
      />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('dashboard', lang)}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl'
              ? 'Pierwsza wersja panelu operacyjnego Adama na bazie aktualnych danych.'
              : 'First operational Adam dashboard based on current data.'}
          </p>
        </div>
        <Button onClick={loadDashboard} variant="outline" className="w-fit">
          <Activity className="w-4 h-4 mr-2" />
          {lang === 'pl' ? 'Odśwież dane' : 'Refresh data'}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 border-primary/20 bg-gradient-to-r from-primary/10 to-transparent">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {lang === 'pl' ? 'Panel zarządczy' : 'Executive view'}
              </p>
              <h2 className="text-lg font-semibold mt-1">
                {lang === 'pl'
                  ? `Witaj ${currentUser?.name || 'w zespole'}, oto priorytety na teraz`
                  : `Welcome ${currentUser?.name || 'back'}, here are current priorities`}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {lang === 'pl'
                  ? 'Dashboard łączy KPI, alerty i działania operacyjne w jednym miejscu.'
                  : 'Dashboard combines KPIs, alerts, and operational actions in one place.'}
              </p>
            </div>
            <Button onClick={() => navigate('/negotiations')}>
              <MessageSquare className="w-4 h-4 mr-2" />
              {lang === 'pl' ? 'Przejdź do rozmów' : 'Go to negotiations'}
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <UserRoundCog className="w-4 h-4 text-primary" />
              {roleCard.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{roleCard.description}</p>
            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={roleCard.onClick}>
              {roleCard.action}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {quickLinks(lang).map((item) => (
          <Button
            key={item.id}
            variant="outline"
            className="h-auto py-4 flex flex-col items-start gap-2"
            onClick={() => navigate(item.to)}
          >
            <item.icon className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium">{item.label}</span>
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.id} className="border-border/90">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  <p className={`text-2xl font-semibold mt-1 ${kpi.valueClass || 'text-foreground'}`}>
                    {kpi.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{kpi.helper}</p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <kpi.icon className="w-4 h-4 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-400" />
              {lang === 'pl' ? 'Alerty krytyczne' : 'Critical alerts'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {executiveAlerts.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {lang === 'pl' ? 'Brak krytycznych alertów.' : 'No critical alerts.'}
              </p>
            )}
            {executiveAlerts.map((alert) => (
              <div key={alert.id} className="rounded-lg border border-border p-3 bg-muted/20">
                <p className="text-sm font-medium">{alert.title}</p>
                <Button variant="ghost" size="sm" className="mt-1 px-0" onClick={alert.onClick}>
                  {alert.action}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <CalendarCheck2 className="w-4 h-4 text-emerald-400" />
              {lang === 'pl' ? 'Plan działań na dziś' : "Today's action plan"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayPlan.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {lang === 'pl' ? 'Brak zadań wygenerowanych automatycznie.' : 'No auto-generated actions yet.'}
              </p>
            )}
            {todayPlan.map((item, idx) => (
              <div key={item.id} className="flex items-start gap-3">
                <Badge variant="outline" className="min-w-6 justify-center">{idx + 1}</Badge>
                <div>
                  <p className="text-sm font-medium">{item.title}</p>
                  {item.hint ? <p className="text-xs text-muted-foreground mt-0.5">{item.hint}</p> : null}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2" data-testid="dashboard-active-negotiations-kpi">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Aktywne negocjacje' : 'Active negotiations'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {negotiations.length === 0 && (
              <p className="text-sm text-muted-foreground py-4">
                {lang === 'pl' ? 'Brak aktywnych negocjacji.' : 'No active negotiations.'}
              </p>
            )}
            {negotiations.map((neg) => (
              <button
                key={neg.id}
                type="button"
                onClick={() => navigate(`/negotiations/${neg.id}`)}
                data-testid={`negotiation-row-${neg.id}`}
                className="w-full text-left flex items-center justify-between rounded-lg border border-border bg-muted/20 px-3 py-2 hover:bg-muted/40 transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{neg.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {neg.contractor_name || (lang === 'pl' ? 'Nieznany kontrahent' : 'Unknown contractor')}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Badge variant="secondary" className="text-xs">
                    {(neg.message_count || 0)} {lang === 'pl' ? 'wiad.' : 'msgs'}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              {t('recentActivity', lang)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(stats?.recent_activity || []).slice(0, 6).map((act, i) => (
              <div key={`${act.timestamp}-${i}`} className="border-l border-primary/30 pl-3">
                <p className="text-xs font-medium">{act.role === 'agent' ? 'Adam' : (act.contractor_name || '—')}</p>
                <p className="text-xs text-muted-foreground truncate">{act.content_preview || '—'}</p>
                <p className="text-xs text-muted-foreground/70 mt-1">{formatDate(act.timestamp)}</p>
              </div>
            ))}
            {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
              <p className="text-sm text-muted-foreground">{t('noData', lang)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="dashboard-recommendations">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            {t('recommendations', lang)}
            <Badge variant="secondary" className="ml-auto text-xs">{recommendations.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {recommendations.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {lang === 'pl' ? 'Brak rekomendacji na ten moment.' : 'No recommendations at the moment.'}
            </p>
          )}
          {recommendations.map((rec, idx) => (
            <button
              key={`${rec.reference_id || idx}-${idx}`}
              type="button"
              className="w-full text-left rounded-lg border border-border px-3 py-3 hover:bg-muted/30 transition-colors"
              onClick={() => {
                if (rec.reference_type === 'negotiation' && rec.reference_id) navigate(`/negotiations/${rec.reference_id}`);
                else if (rec.reference_type === 'deal') navigate('/pipeline');
                else if (rec.reference_type === 'contractors') navigate('/contractors');
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{rec.title || (lang === 'pl' ? 'Rekomendacja' : 'Recommendation')}</p>
                  <p className="text-xs text-muted-foreground mt-1">{rec.description || '—'}</p>
                </div>
                <Badge className={`text-xs border ${recommendationColors[rec.priority] || recommendationColors.low}`}>
                  {rec.priority || 'low'}
                </Badge>
              </div>
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
